/**
 * Keyboard shortcut registry: one scoped catalogue for the whole app (pure, no DOM/React).
 *
 * Scopes: `global`, `page:<name>`, `dialog:<name>`, `component:<name>`. A modal dialog owns the
 * keyboard while open: page and component bindings are suspended, and only `global` bindings
 * flagged `inDialogs` (⌘K) pass through. Registering a binding that overlaps another one, or a
 * combo the browser/OS owns (⌘N, ⌘T, ⌘W, ⌘L, ⌘R, ⌘1–9 …), throws — so a bad catalogue fails
 * on import and in the tests, never in front of a user.
 *
 * Combos are `+`-joined: `Mod+Enter`, `Alt+D`, `Shift+ArrowUp`, `c`, `?`. `Mod` is ⌘ on Mac and
 * Ctrl elsewhere. A space makes a chord (`g i`); chords are documentation-only (`external`).
 */
const NAMED = {
    esc: 'Escape',
    escape: 'Escape',
    enter: 'Enter',
    return: 'Enter',
    space: ' ',
    up: 'ArrowUp',
    down: 'ArrowDown',
    left: 'ArrowLeft',
    right: 'ArrowRight',
};
export function parseCombo(combo) {
    if (combo.includes(' ') && combo.trim().length > 1) {
        return { ...parseCombo(combo.split(' ')[0] ?? ''), chord: true };
    }
    const parts = combo.split('+');
    const raw = parts.pop() || '+';
    const c = {
        mod: false,
        ctrl: false,
        meta: false,
        alt: false,
        shift: false,
        key: '',
        chord: false,
    };
    for (const m of parts) {
        const k = m.toLowerCase();
        if (k === 'mod')
            c.mod = true;
        else if (k === 'ctrl' || k === 'control')
            c.ctrl = true;
        else if (k === 'meta' || k === 'cmd')
            c.meta = true;
        else if (k === 'alt' || k === 'option')
            c.alt = true;
        else if (k === 'shift')
            c.shift = true;
        else
            throw new Error(`Unknown modifier "${m}" in "${combo}"`);
    }
    c.key = NAMED[raw.toLowerCase()] ?? (raw.length === 1 ? raw.toLowerCase() : raw);
    return c;
}
/** Canonical string: modifiers in fixed order, ⌘/Ctrl folded into Mod. */
export function canonical(combo) {
    const c = parseCombo(combo);
    const mods = [
        c.mod || c.ctrl || c.meta ? 'Mod' : '',
        c.alt ? 'Alt' : '',
        c.shift ? 'Shift' : '',
    ].filter(Boolean);
    const key = c.chord ? combo.trim().toLowerCase() : c.key;
    return c.chord ? key : [...mods, key].join('+');
}
export const hasModifier = (combo) => {
    const c = parseCombo(combo);
    return c.mod || c.ctrl || c.meta || c.alt;
};
export const isMacPlatform = (platform = typeof navigator === 'undefined' ? '' : navigator.platform) => /Mac|iPhone|iPad|iPod/i.test(platform);
/** Does this keydown match the (single-step) combo? Alt+letter/digit matches on `code`. */
export function matchEvent(combo, e, mac = isMacPlatform()) {
    const c = parseCombo(combo);
    if (c.chord)
        return false;
    const ctrl = c.ctrl || (c.mod && !mac);
    const meta = c.meta || (c.mod && mac);
    if (e.ctrlKey !== ctrl || e.metaKey !== meta || e.altKey !== c.alt)
        return false;
    const printable = c.key.length === 1;
    const wordKey = /^[a-z0-9]$/.test(c.key);
    // ⇧ is implied by symbols like "?"; everything else must agree exactly.
    if (!(printable && !wordKey) && e.shiftKey !== c.shift)
        return false;
    if (c.alt && wordKey) {
        const code = /\d/.test(c.key) ? `Digit${c.key}` : `Key${c.key.toUpperCase()}`;
        // ⌥+letter types a symbol on macOS, so `key` is unusable; `code` is layout-independent.
        return e.code ? e.code === code : e.key.toLowerCase() === c.key;
    }
    return printable ? e.key.toLowerCase() === c.key : e.key === c.key;
}
const GLYPH_MAC = {
    Mod: '⌘',
    Ctrl: '⌃',
    Alt: '⌥',
    Shift: '⇧',
    Enter: '↵',
    Escape: 'Esc',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    ' ': 'Space',
};
const GLYPH_OTHER = {
    Mod: 'Ctrl',
    Enter: 'Enter',
    Escape: 'Esc',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    ' ': 'Space',
};
/** Platform-aware label: `⌘↵`, `⌥D` on Mac; `Ctrl+Enter`, `Alt+D` elsewhere. */
export function formatCombo(combo, mac = isMacPlatform()) {
    if (combo.includes(' ') && combo.trim().length > 1)
        return combo
            .split(' ')
            .map((p) => formatCombo(p, mac))
            .join(' ');
    const parts = combo.split('+');
    const raw = parts.pop() || '+';
    const glyph = mac ? GLYPH_MAC : GLYPH_OTHER;
    const mods = parts.map((m) => {
        const k = m.toLowerCase();
        const name = k === 'mod' || k === 'cmd' || k === 'meta'
            ? 'Mod'
            : k === 'option' || k === 'alt'
                ? 'Alt'
                : k === 'ctrl' || k === 'control'
                    ? 'Ctrl'
                    : 'Shift';
        return glyph[name] ?? name;
    });
    const named = NAMED[raw.toLowerCase()];
    const keyName = named ?? raw;
    const key = glyph[keyName] ?? (keyName.length === 1 ? keyName.toUpperCase() : keyName);
    return mac ? [...mods, key].join('') : [...mods, key].join('+');
}
/** `aria-keyshortcuts` value ("Meta+Enter Control+Enter"; Mod names both platforms). */
export function ariaKeyShortcuts(combo) {
    const out = [];
    const parts = combo.split('+');
    const raw = parts.pop() || '+';
    const key = NAMED[raw.toLowerCase()] ?? (raw.length === 1 ? raw.toUpperCase() : raw);
    const mods = parts.map((m) => m.toLowerCase());
    const rest = [
        mods.includes('alt') || mods.includes('option') ? 'Alt' : '',
        mods.includes('shift') ? 'Shift' : '',
    ].filter(Boolean);
    if (mods.includes('mod'))
        for (const m of ['Meta', 'Control'])
            out.push([m, ...rest, key].join('+'));
    else {
        const base = [
            mods.includes('ctrl') || mods.includes('control') ? 'Control' : '',
            mods.includes('meta') || mods.includes('cmd') ? 'Meta' : '',
            ...rest,
        ].filter(Boolean);
        out.push([...base, key].join('+'));
    }
    return out.join(' ');
}
// ---- browser / OS reserved combos ------------------------------------------------------------
const RESERVED_MOD_KEYS = 'n t w l r q p s f d h m a c v x z y o u j g b i e'.split(' ');
const RESERVED_MOD_SHIFT_KEYS = 'n t w r i j delete b'.split(' ');
/**
 * Why a combo can't be used (browser tab, OS or text-editing owns it), or undefined. ⌘/Ctrl+1–9
 * switch browser tabs, so Lanes uses ⌥1–9 there; a def flagged `standalone` may take them (PWA).
 */
export function reservedReason(combo, standalone = false) {
    const c = parseCombo(combo);
    if (c.chord)
        return undefined;
    const mod = c.mod || c.ctrl || c.meta;
    if (mod && !c.alt && !c.shift) {
        if (RESERVED_MOD_KEYS.includes(c.key))
            return `⌘/Ctrl+${c.key.toUpperCase()} belongs to the browser or OS`;
        if (/^[0-9]$/.test(c.key))
            return standalone && c.key !== '0' ? undefined : '⌘/Ctrl+digit switches browser tabs or zoom';
        if (['+', '-', '=', '[', ']', 'Tab'].includes(c.key))
            return 'zoom, history and tab switching belong to the browser';
    }
    if (mod && c.shift && !c.alt && RESERVED_MOD_SHIFT_KEYS.includes(c.key))
        return '⌘/Ctrl+⇧ window and tab commands belong to the browser';
    if (c.alt &&
        !mod &&
        !c.shift &&
        (c.key === 'ArrowLeft' || c.key === 'ArrowRight' || c.key === 'Home'))
        return '⌥←/→ and ⌥Home are browser history and home';
    if (!mod && !c.alt && /^F([1-9]|1[0-2])$/.test(c.key))
        return 'function keys belong to the browser';
    if (c.ctrl && c.key === 'Tab')
        return 'Ctrl+Tab switches browser tabs';
    return undefined;
}
// ---- registry --------------------------------------------------------------------------------
const kind = (s) => s.split(':')[0];
/** Can both bindings be live for one keypress? (Same scope, or one flows into the other.) */
export function scopesOverlap(a, b) {
    if (a.scope === b.scope)
        return true;
    const [ka, kb] = [kind(a.scope), kind(b.scope)];
    if (ka === 'global' || kb === 'global') {
        const other = ka === 'global' ? b : a;
        const glob = ka === 'global' ? a : b;
        return kind(other.scope) !== 'dialog' || !!glob.inDialogs;
    }
    // Pages, dialogs and components: distinct names never overlap, except a component lives on
    // every page (it has no single parent) but never inside a dialog.
    if ((ka === 'component' && kb === 'page') || (ka === 'page' && kb === 'component'))
        return true;
    return false;
}
export class ShortcutConflictError extends Error {
}
export class ShortcutRegistry {
    defs = [];
    constructor(defs = []) {
        for (const d of defs)
            this.register(d);
    }
    register(def) {
        if (this.defs.some((d) => d.id === def.id))
            throw new ShortcutConflictError(`Duplicate shortcut id "${def.id}"`);
        if (def.keys.length === 0)
            throw new Error(`Shortcut "${def.id}" has no keys`);
        for (const k of def.keys) {
            const c = parseCombo(k);
            if (c.chord && !def.external)
                throw new Error(`Chord "${k}" on "${def.id}" must be marked external`);
            const why = def.external ? undefined : reservedReason(k, def.standalone);
            if (why)
                throw new ShortcutConflictError(`"${def.id}" uses reserved ${k}: ${why}`);
        }
        const hit = this.conflictsOf(def)[0];
        if (hit)
            throw new ShortcutConflictError(`"${def.id}" (${def.scope}) conflicts with "${hit.b.id}" (${hit.b.scope}) on ${hit.combo}`);
        this.defs.push(def);
    }
    conflictsOf(def) {
        const out = [];
        for (const other of this.defs) {
            if (other.overrides === def.id || def.overrides === other.id)
                continue;
            if (!scopesOverlap(def, other))
                continue;
            for (const k of def.keys)
                if (other.keys.some((o) => canonical(o) === canonical(k)))
                    out.push({ a: def, b: other, combo: canonical(k) });
        }
        return out;
    }
    /** Every overlapping pair in the catalogue (empty when healthy). */
    conflicts() {
        const seen = [];
        const out = [];
        for (const d of this.defs) {
            const probe = new ShortcutRegistry();
            probe.defs = seen;
            out.push(...probe.conflictsOf(d));
            seen.push(d);
        }
        return out;
    }
    all() {
        return this.defs;
    }
    get(id) {
        return this.defs.find((d) => d.id === id);
    }
    /**
     * The binding a keydown resolves to. `active` lists the live scopes (globals are implicit);
     * `bound` says which ids currently have a handler. Plain (modifier-less) keys never resolve
     * while `typing`; the innermost scope wins.
     */
    resolve(e, o) {
        const dialogs = o.active.filter((s) => kind(s) === 'dialog');
        const top = dialogs[dialogs.length - 1];
        let best;
        for (const d of this.defs) {
            if (d.external || !o.bound(d.id))
                continue;
            const k = kind(d.scope);
            let rank;
            if (top) {
                if (d.scope === top)
                    rank = 3;
                else if (k === 'global' && d.inDialogs)
                    rank = 0;
                else
                    continue;
            }
            else if (k === 'global')
                rank = 0;
            else if (k === 'dialog' || !o.active.includes(d.scope))
                continue;
            else
                rank = k === 'component' ? 2 : 1;
            const hit = d.keys.find((key) => matchEvent(key, e, o.mac) && !(o.typing && !hasModifier(key)));
            if (hit && (!best || rank > best.rank))
                best = { def: d, rank };
        }
        return best?.def;
    }
}
//# sourceMappingURL=registry.js.map